import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {BehaviorSubject, distinctUntilChanged, Subject, Subscription, takeUntil} from 'rxjs';
import {Backend} from '@generated-backend-types';
import {filter} from 'rxjs/operators';
import {PrivateUploaderService} from './form-inputs/private-file-upload-field/services/private-uploader.service';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import PrivateAttachment = Backend.App.Resources.Dashboard.Administration.PrivateAttachment;
import PrivateAttachmentAttachableTypeEnum = Backend.App.Enums.Administration.Attachments.PrivateAttachmentAttachableTypeEnum;
import PrivateAttachmentTypeEnum = Backend.App.Enums.Administration.Attachments.PrivateAttachmentTypeEnum;
import PrivateAttachmentMetaData = Backend.App.Data.Meta.PrivateAttachmentMetaData;

@Component({
  selector: 'app-file',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export abstract class AbstractFileManagerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public uploadTriggerType: 'on-push' | 'on-change' = 'on-change';
  @Input() public uploadPushTrigger$?: Subject<void>;
  @Input() public itemName: string = '';
  @Input() public privateAttachments: PrivateAttachment[] = [];
  @Input() public privateAttachmentAttachableId!: string;
  @Input() public privateAttachmentAttachableType!: PrivateAttachmentAttachableTypeEnum;
  @Input() public privateAttachmentType!: PrivateAttachmentTypeEnum;
  @Input() public privateAttachmentMeta?: PrivateAttachmentMetaData;
  @Output() public uploadFinished: EventEmitter<void> = new EventEmitter();
  @ViewChild('fileInput') public fileInput!: ElementRef<HTMLInputElement>;
  public uploadsAllowed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  protected onDestroy$: Subject<void> = new Subject();
  private uploadPushTriggerSubscription?: Subscription;
  private fileUploadInputEventTarget?: HTMLInputElement;

  protected constructor(
    protected readonly cdr: ChangeDetectorRef,
    protected readonly privateUploadService: PrivateUploaderService,
    protected readonly toastr: ToastrService,
    protected readonly translate: TranslateService,
  ) {
  }

  public ngOnInit() {
    this.privateUploadService.fileUploaded.pipe(
      takeUntil(this.onDestroy$),
      filter(
        fileUpload =>
          fileUpload.finished &&
          fileUpload.attachable_type === this.privateAttachmentAttachableType &&
          fileUpload.attachable_id === this.privateAttachmentAttachableId &&
          fileUpload.type === this.privateAttachmentType &&
          !!fileUpload.attachment
      ),
      distinctUntilChanged()
    ).subscribe({
      next: fileUpload => {
        this.uploadFinished.emit();
        this.toastr.success(
          this.translate.instant('components.private_file_upload_card.upload.success', {itemName: this.itemName})
        )
        this.privateAttachments.push(fileUpload.attachment!);
        this.checkUploadsAllowedState();
        this.cdr.markForCheck();
      }
    })

    this.privateUploadService.fileErrors.pipe(
      takeUntil(this.onDestroy$),
      filter(
        fileUpload => {
          return fileUpload.error &&
            fileUpload.attachable_type === this.privateAttachmentAttachableType &&
            fileUpload.attachable_id === this.privateAttachmentAttachableId &&
            fileUpload.type === this.privateAttachmentType
        }),
      distinctUntilChanged()
    ).subscribe({
      next: fileUpload => {
        this.uploadFinished.emit();
        this.checkUploadsAllowedState();
        this.toastr.error(fileUpload.errorMessage ?? this.translate.instant('components.private_file_upload_card.upload.error', {itemName: this.itemName}));
      }
    })

    this.subscribeToUploadPushTrigger();

    this.checkUploadsAllowedState();
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('uploadPushTrigger$' in changes) {
      this.subscribeToUploadPushTrigger();
    }
  }

  public fileSelected(event: Event) {
    const target = event.target as HTMLInputElement;

    this.fileUploadInputEventTarget = target;

    if (this.uploadTriggerType === 'on-change') {
      if (!this.privateAttachmentType || !this.privateAttachmentAttachableType || !this.privateAttachmentAttachableId) return;

      if (target?.files) {
        this.privateUploadService.uploadFiles(
          {
            files: target.files,
            type: this.privateAttachmentType,
            attachable_type: this.privateAttachmentAttachableType,
            attachable_id: this.privateAttachmentAttachableId
          }
        );
        if (this.fileInput) this.fileInput.nativeElement.value = '';
        if (this.fileInput) this.fileInput.nativeElement.files = null;
      } else {
        // We have to fire this within a timeout since this event could be fired before it is even subscribed to
        setTimeout(() => {
          this.uploadFinished.emit();
        }, 100)
      }
    }
  }

  public openFile(a: PrivateAttachment): void {
    if (a.url) {
      window.open(a.url, '_blank', 'noopener,noreferrer');
    }
  }

  protected abstract checkUploadsAllowedState(): void;

  private subscribeToUploadPushTrigger(): void {
    this.uploadPushTriggerSubscription?.unsubscribe();
    this.uploadPushTriggerSubscription = this.uploadPushTrigger$?.pipe(
      filter(() => this.uploadTriggerType === 'on-push'),
      takeUntil(this.onDestroy$)
    ).subscribe({
      next: () => {
        if (
          this.fileUploadInputEventTarget?.files
        ) {
          this.privateUploadService.uploadFiles(
            {
              files: this.fileUploadInputEventTarget.files,
              type: this.privateAttachmentType,
              attachable_type: this.privateAttachmentAttachableType,
              attachable_id: this.privateAttachmentAttachableId
            }
          );
          if (this.fileInput) this.fileInput.nativeElement.value = '';
          if (this.fileInput) this.fileInput.nativeElement.files = null;
        } else {
          // We have to fire this within a timeout since this event could be fired before it is even subscribed to
          setTimeout(() => {
            this.uploadFinished.emit();
          }, 100)
        }
      }
    })
  }
}
